/**
 * Шаринг результата (S4). В Mini App: `POST /api/share` → `Telegram.WebApp.shareMessage(id)` (чат) или
 * `shareToStory(imageUrl)`. В обычном браузере (ISSUE-144) — ссылка на игру: системное окно `navigator.share`, где оно
 * есть; иначе ссылка в буфер и окно «Ссылка скопирована»; без буфера — окно с полем, откуда её взять руками.
 * Убийства знает бой (`game/connect.ts`): пока они не переданы в `share({ wave, kills })`, кнопка молчит.
 */
import { analytics } from "../analytics";
import type { ApiTransport } from "../api";
import { t } from "../i18n";
import { setWindowActions, windowActions } from "../ui/windows/actions";
import { fetchLoginBot } from "./login";
import { appStore, openWindow } from "./store";
import { loadTelegramWebApp } from "./telegram";

export async function shareResult(
  api: ApiTransport,
  info: { wave: number; kills: number; target?: "chat" | "story" },
): Promise<boolean> {
  const tg = window.Telegram?.WebApp ?? (await loadTelegramWebApp());
  if (!tg || info.kills < 1) return false;
  const target = info.target ?? (tg.shareMessage ? "chat" : "story");
  analytics().share(target, info.wave);
  try {
    const res = await api.call("share", { target, wave: info.wave, kills: info.kills });
    if (target === "chat") {
      if (!res.preparedMessageId || !tg.shareMessage) return false;
      tg.shareMessage(res.preparedMessageId);
    } else {
      if (!tg.shareToStory) return false;
      tg.shareToStory(res.imageUrl);
    }
    return true;
  } catch {
    return false; // шаринг не критичен для игры
  }
}

/** Ссылка-приглашение: выбор чата в Telegram, на сайте — новая вкладка с той же страницей Telegram. */
export function inviteFriend(url: string, text: string): void {
  const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  const tg = window.Telegram?.WebApp;
  if (tg?.openTelegramLink) tg.openTelegramLink(share);
  else window.open(share, "_blank", "noopener");
}

export interface WebShareDeps {
  /** Ссылка-приглашение `fr_<код>` у аккаунта Telegram; у гостя `null`. */
  inviteUrl(): Promise<string | null>;
  /** Username бота без `@` — для ссылки `startapp=sh_<N>`; `null` — ссылка на сайт. */
  botName(): Promise<string | null>;
  siteUrl: string;
  nav: Pick<Navigator, "share" | "clipboard"> | undefined;
  openWindow(route: { kind: "shareLink"; url: string; text: string; copied: boolean }): void;
}

/** Ссылка, которой делятся из браузера: приглашение, иначе Mini App с меткой шаринга, иначе сайт. */
export async function webShareUrl(deps: Pick<WebShareDeps, "inviteUrl" | "botName" | "siteUrl">, kills: number) {
  const invite = await deps.inviteUrl().catch(() => null);
  if (invite) return invite;
  const bot = await deps.botName().catch(() => null);
  return bot ? `https://t.me/${bot}?startapp=sh_${Math.max(0, Math.trunc(kills))}` : deps.siteUrl;
}

/** Текст со ссылкой — в буфер; `false`, если буфера нет или браузер отказал. */
export async function copyText(nav: WebShareDeps["nav"], text: string): Promise<boolean> {
  if (!nav?.clipboard?.writeText) return false;
  try {
    await nav.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Шаринг в браузере: `native` — системное окно, `copied` — в буфере, `shown` — окно с полем, `cancelled` — закрыл сам. */
export async function shareWeb(
  deps: WebShareDeps,
  info: { kills: number },
): Promise<"native" | "copied" | "shown" | "cancelled"> {
  const url = await webShareUrl(deps, info.kills);
  const text =
    info.kills > 0 ? t("win.shareLink.message", { n: info.kills.toLocaleString() }) : t("win.shareLink.messageNoKills");
  if (deps.nav?.share) {
    try {
      await deps.nav.share({ title: "New Fun Game", text, url });
      return "native";
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return "cancelled";
      // NotAllowedError (жест истёк, пока ждали ссылку) и прочее — к буферу
    }
  }
  const copied = await copyText(deps.nav, `${text} ${url}`);
  deps.openWindow({ kind: "shareLink", url, text, copied });
  return copied ? "copied" : "shown";
}

/** Подключает кнопки «Поделиться» окон и десктопа к API. */
export function setupShare(api: ApiTransport): void {
  // отладка окон (`?mock=1`) пишет вызовы в журнал сама — не перекрываем её колбэки
  if (window.__nfgWindows) return;
  const miniApp = () => appStore.get().miniApp;
  let bot: Promise<string | null> | null = null;
  const botName = () => (bot ??= fetchLoginBot());
  // имя бота нужно сразу по нажатию: пока ждём сеть, браузер может забыть жест и не дать `navigator.share`
  if (!miniApp()) void botName();
  const web: WebShareDeps = {
    inviteUrl: async () => {
      if (typeof appStore.get().player?.player.telegramId !== "number") return null;
      return (await windowActions.get().friends())?.inviteUrl ?? null;
    },
    botName,
    siteUrl: window.location.origin + window.location.pathname,
    nav: typeof navigator === "undefined" ? undefined : navigator,
    openWindow: (route) => openWindow(route),
  };
  setWindowActions({
    copyShareLink: (text, url) => copyText(web.nav, `${text} ${url}`),
    share: (info) => {
      if (info.kills === undefined) return;
      if (!miniApp()) {
        analytics().share("link", info.wave);
        void shareWeb(web, { kills: info.kills });
        return;
      }
      void shareResult(api, { wave: info.wave, kills: info.kills, ...(info.target ? { target: info.target } : {}) });
    },
    inviteFriend: (url, place = "league") => {
      analytics().friendInvite(place);
      inviteFriend(url, t("win.league.inviteText"));
    },
  });
}
